/**
 * usePurchaseOrders Hook
 *
 * React Query hooks for purchase orders.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  purchaseOrdersService,
  type QueryPurchaseOrdersDto,
  type CreatePurchaseOrderDto,
  type UpdatePurchaseOrderDto,
  type AddOrderItemDto,
  type UpdateOrderItemDto,
} from '../services/purchase-orders.service';

/**
 * Query key factory for purchase orders
 */
export const orderKeys = {
  all: ['purchase-orders'] as const,
  lists: () => [...orderKeys.all, 'list'] as const,
  list: (params: QueryPurchaseOrdersDto) => [...orderKeys.lists(), params] as const,
  details: () => [...orderKeys.all, 'detail'] as const,
  detail: (id: string) => [...orderKeys.details(), id] as const,
};

/**
 * Hook to fetch purchase orders list with pagination
 */
export function usePurchaseOrders(params: QueryPurchaseOrdersDto) {
  return useQuery({
    queryKey: orderKeys.list(params),
    queryFn: () => purchaseOrdersService.list(params),
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook to fetch single purchase order
 */
export function usePurchaseOrder(id: string | null) {
  return useQuery({
    queryKey: orderKeys.detail(id!),
    queryFn: () => purchaseOrdersService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to create a purchase order
 */
export function useCreatePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePurchaseOrderDto) => purchaseOrdersService.create(data),
    onSuccess: (order) => {
      queryClient.invalidateQueries({ queryKey: orderKeys.all });
      toast.success(`Commande ${order.orderNumber} creee`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la creation');
    },
  });
}

/**
 * Hook to update a purchase order
 */
export function useUpdatePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePurchaseOrderDto }) =>
      purchaseOrdersService.update(id, data),
    onSuccess: (order) => {
      queryClient.invalidateQueries({ queryKey: orderKeys.all });
      toast.success(`Commande ${order.orderNumber} mise a jour`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour');
    },
  });
}

/**
 * Hook to delete a purchase order
 */
export function useDeletePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => purchaseOrdersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.all });
      toast.success('Commande supprimee');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la suppression');
    },
  });
}

/**
 * Hook to add an item to a purchase order
 */
export function useAddOrderItem() {
  return useMutation({
    mutationFn: ({ orderId, data }: { orderId: string; data: AddOrderItemDto }) =>
      purchaseOrdersService.addItem(orderId, data),
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? "Erreur lors de l'ajout");
    },
  });
}

/**
 * Hook to update an item in a purchase order
 */
export function useUpdateOrderItem() {
  return useMutation({
    mutationFn: ({
      orderId,
      itemId,
      data,
    }: {
      orderId: string;
      itemId: string;
      data: UpdateOrderItemDto;
    }) => purchaseOrdersService.updateItem(orderId, itemId, data),
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour');
    },
  });
}

/**
 * Hook to remove an item from a purchase order
 */
export function useRemoveOrderItem() {
  return useMutation({
    mutationFn: ({ orderId, itemId }: { orderId: string; itemId: string }) =>
      purchaseOrdersService.removeItem(orderId, itemId),
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la suppression');
    },
  });
}

/**
 * Hook to send a purchase order
 */
export function useSendPurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => purchaseOrdersService.send(id),
    onSuccess: (order) => {
      queryClient.invalidateQueries({ queryKey: orderKeys.all });
      toast.success(`Commande ${order.orderNumber} envoyee`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? "Erreur lors de l'envoi");
    },
  });
}

/**
 * Hook to cancel a purchase order
 */
export function useCancelPurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => purchaseOrdersService.cancel(id),
    onSuccess: (order) => {
      queryClient.invalidateQueries({ queryKey: orderKeys.all });
      toast.success(`Commande ${order.orderNumber} annulee`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? "Erreur lors de l'annulation");
    },
  });
}

/**
 * Hook to duplicate a purchase order
 */
export function useDuplicatePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => purchaseOrdersService.duplicate(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: orderKeys.all });
      toast.success(`Commande ${result.order.orderNumber} creee`);
      if (result.warnings.length > 0) {
        result.warnings.forEach((w) => {
          toast(`${w.productName}: ${w.reason}`, { icon: '!' });
        });
      }
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la duplication');
    },
  });
}
